import type { User } from '@/types';
import { API_ENDPOINTS } from '@/constants';
import { apiClient } from '@/services/api';
import { getStoredUser, setStoredUser } from '@/services/auth';

export const SETUP_TOUR_COMPLETION_CHANGED_EVENT = 'setup-tour:completion-changed';

type UserProfileResponse = {
  user: User;
};

type SetupTourOnboardingStatus = 'completed' | 'skipped';

export const isSetupTourCompleted = (user: User | null, onboardingKey: string): boolean =>
  user?.onboardingState?.[onboardingKey]?.status === 'completed';

export const isSetupTourCompletedForCurrentUser = (onboardingKey: string): boolean =>
  isSetupTourCompleted(getStoredUser(), onboardingKey);

export const fetchCurrentUserProfile = async (): Promise<User> => {
  const response = await apiClient.get<UserProfileResponse>(API_ENDPOINTS.USER_PROFILE);
  setStoredUser(response.data.user);
  return response.data.user;
};

export const recordSetupTourStatus = async (
  tourId: string,
  onboardingKey: string,
  status: SetupTourOnboardingStatus,
): Promise<User> => {
  const response = await apiClient.post<UserProfileResponse>(API_ENDPOINTS.USER_ONBOARDING, {
    tourKey: onboardingKey,
    status,
  });

  setStoredUser(response.data.user);
  window.dispatchEvent(
    new CustomEvent(SETUP_TOUR_COMPLETION_CHANGED_EVENT, {
      detail: { tourId, status },
    }),
  );

  return response.data.user;
};

export const markSetupTourCompleted = (tourId: string, onboardingKey: string): Promise<User> =>
  recordSetupTourStatus(tourId, onboardingKey, 'completed');

export const markSetupTourSkipped = (tourId: string, onboardingKey: string): Promise<User> =>
  recordSetupTourStatus(tourId, onboardingKey, 'skipped');
